import { useCallback, useEffect, useRef, useState } from 'react';

import type { CanvassingSessionDetailQuery } from '../../graphql/queries/canvassingSessionDetail.generated';
import type { CanvassingSessionDoorVisitListQuery } from '../../graphql/queries/canvassingSessionDoorVisitList.generated';
import type { CanvassingSessionPipelineJobListQuery } from '../../graphql/queries/canvassingSessionPipelineJobList.generated';
import {
  fetchCanvassingSessionDetailCycle,
  toCanvassingSessionRequestError,
  type CanvassingSessionFetchImpl,
  type CanvassingSessionRequestError,
} from './canvassingSessionApi';

export type CanvassingSessionDetail = NonNullable<CanvassingSessionDetailQuery['canvassingSessionDetail']>;
export type CanvassingSessionPipelineJobs = NonNullable<CanvassingSessionPipelineJobListQuery['canvassingSessionPipelineJobList']>;
export type CanvassingSessionDoorVisits = NonNullable<CanvassingSessionDoorVisitListQuery['canvassingSessionDoorVisitList']>;

export const CANVASSING_SESSION_DETAIL_DEFAULT_ROWS_PER_PAGE = 25;
export const CANVASSING_SESSION_POLL_INTERVAL_MS = 5_000;
export const CANVASSING_SESSION_POLL_DEADLINE_MS = 5 * 60_000;

export type CanvassingSessionDetailScheduler = {
  now: () => number;
  setTimeout: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
  clearTimeout: (timer: ReturnType<typeof setTimeout>) => void;
};

const defaultScheduler: CanvassingSessionDetailScheduler = {
  now: () => Date.now(),
  setTimeout: (callback, delayMs) => setTimeout(callback, delayMs),
  clearTimeout: timer => clearTimeout(timer),
};

export type UseCanvassingSessionDetailOptions = {
  sessionId: string | undefined;
  fetchImpl?: CanvassingSessionFetchImpl;
  scheduler?: CanvassingSessionDetailScheduler;
};

export type CanvassingSessionDetailRefetchOptions = {
  clampEmptyPipelineJobsPage?: boolean;
};

export type UseCanvassingSessionDetailResult = {
  data: CanvassingSessionDetail | null;
  pipelineJobs: CanvassingSessionPipelineJobs;
  doorVisits: CanvassingSessionDoorVisits;
  loading: boolean;
  refreshing: boolean;
  manualRefreshing: boolean;
  error: CanvassingSessionRequestError | null;
  backgroundError: CanvassingSessionRequestError | null;
  pipelineJobsPage: number;
  pipelineJobsRowsPerPage: number;
  doorVisitsPage: number;
  doorVisitsRowsPerPage: number;
  setPipelineJobsPage: (page: number) => void;
  setPipelineJobsRowsPerPage: (rowsPerPage: number) => void;
  setDoorVisitsPage: (page: number) => void;
  setDoorVisitsRowsPerPage: (rowsPerPage: number) => void;
  refetch: (options?: CanvassingSessionDetailRefetchOptions) => void;
};

type Pagination = {
  page: number;
  rowsPerPage: number;
};

type RefreshKind = 'initial' | 'manual' | 'pagination' | 'automatic';

type RefreshRequest = {
  kind: RefreshKind;
  sessionId: string;
  pipelineJobs: Pagination;
  doorVisits: Pagination;
  clampEmptyPipelineJobsPage?: boolean;
};

type ActiveCycle = RefreshRequest & {
  controller: AbortController;
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const emptyPipelineJobs = (): CanvassingSessionPipelineJobs => ({
  __typename: 'CanvassingSessionPipelineJobListResult',
  items: [],
  total: 0,
});

const emptyDoorVisits = (): CanvassingSessionDoorVisits => ({
  __typename: 'CanvassingSessionDoorVisitListResult',
  items: [],
  total: 0,
});

const normalizePage = (page: number): number => (Number.isSafeInteger(page) && page > 0 ? page : 0);

const normalizeRowsPerPage = (rowsPerPage: number): number =>
  Number.isSafeInteger(rowsPerPage) && rowsPerPage > 0 ? rowsPerPage : CANVASSING_SESSION_DETAIL_DEFAULT_ROWS_PER_PAGE;

export const resolveClampedPipelineJobsPage = ({
  itemCount,
  page,
  rowsPerPage,
  total,
}: {
  itemCount: number;
  page: number;
  rowsPerPage: number;
  total: number;
}): number | null => {
  if (itemCount > 0 || page <= 0) return null;
  if (total <= 0) return 0;

  const lastNonEmptyPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
  return lastNonEmptyPage === page ? null : lastNonEmptyPage;
};

export const isCanvassingSessionId = (sessionId: string | undefined): sessionId is string =>
  typeof sessionId === 'string' && UUID_PATTERN.test(sessionId);

export const useCanvassingSessionDetail = ({
  sessionId,
  fetchImpl,
  scheduler = defaultScheduler,
}: UseCanvassingSessionDetailOptions): UseCanvassingSessionDetailResult => {
  const isValidSessionId = isCanvassingSessionId(sessionId);
  const [data, setData] = useState<CanvassingSessionDetail | null>(null);
  const [pipelineJobs, setPipelineJobs] = useState<CanvassingSessionPipelineJobs>(emptyPipelineJobs);
  const [doorVisits, setDoorVisits] = useState<CanvassingSessionDoorVisits>(emptyDoorVisits);
  const [loading, setLoading] = useState(isValidSessionId);
  const [refreshing, setRefreshing] = useState(false);
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const [error, setError] = useState<CanvassingSessionRequestError | null>(null);
  const [backgroundError, setBackgroundError] = useState<CanvassingSessionRequestError | null>(null);
  const [automaticPollingEnabled, setAutomaticPollingEnabled] = useState(true);
  const [deadline, setDeadline] = useState<number | null>(null);
  const [pipelineJobsPage, setPipelineJobsPageState] = useState(0);
  const [pipelineJobsRowsPerPage, setPipelineJobsRowsPerPageState] = useState(CANVASSING_SESSION_DETAIL_DEFAULT_ROWS_PER_PAGE);
  const [doorVisitsPage, setDoorVisitsPageState] = useState(0);
  const [doorVisitsRowsPerPage, setDoorVisitsRowsPerPageState] = useState(CANVASSING_SESSION_DETAIL_DEFAULT_ROWS_PER_PAGE);

  const dataRef = useRef<CanvassingSessionDetail | null>(data);
  const sessionIdRef = useRef(sessionId);
  const deadlineRef = useRef<number | null>(deadline);
  const activeCycleRef = useRef<ActiveCycle | null>(null);
  const refreshInFlightRef = useRef(false);
  const pendingClampedRefetchRef = useRef(false);
  const pipelineJobsPaginationRef = useRef<Pagination>({
    page: pipelineJobsPage,
    rowsPerPage: pipelineJobsRowsPerPage,
  });
  const doorVisitsPaginationRef = useRef<Pagination>({
    page: doorVisitsPage,
    rowsPerPage: doorVisitsRowsPerPage,
  });

  dataRef.current = data;
  sessionIdRef.current = sessionId;
  deadlineRef.current = deadline;
  pipelineJobsPaginationRef.current = { page: pipelineJobsPage, rowsPerPage: pipelineJobsRowsPerPage };
  doorVisitsPaginationRef.current = { page: doorVisitsPage, rowsPerPage: doorVisitsRowsPerPage };

  const abortActiveCycle = useCallback((): void => {
    const activeCycle = activeCycleRef.current;
    if (activeCycle === null) return;

    activeCycleRef.current = null;
    refreshInFlightRef.current = false;
    setManualRefreshing(false);
    activeCycle.controller.abort();
  }, []);

  const runRefreshCycle = useCallback(
    async (request: RefreshRequest): Promise<void> => {
      if (!isCanvassingSessionId(request.sessionId) || request.sessionId !== sessionIdRef.current) return;
      if (refreshInFlightRef.current) return;

      const controller = new AbortController();
      const activeCycle: ActiveCycle = { ...request, controller };
      activeCycleRef.current = activeCycle;
      refreshInFlightRef.current = true;

      if (request.kind === 'initial') {
        setLoading(true);
        setError(null);
      } else {
        setRefreshing(true);
        setManualRefreshing(request.kind === 'manual');
      }

      try {
        const cycle = await fetchCanvassingSessionDetailCycle({
          sessionId: request.sessionId,
          pipelineJobs: {
            first: request.pipelineJobs.rowsPerPage,
            offset: request.pipelineJobs.page * request.pipelineJobs.rowsPerPage,
          },
          doorVisits: {
            first: request.doorVisits.rowsPerPage,
            offset: request.doorVisits.page * request.doorVisits.rowsPerPage,
          },
          signal: controller.signal,
          fetchImpl,
        });

        if (controller.signal.aborted || request.sessionId !== sessionIdRef.current) return;

        const isCurrentPagination =
          pipelineJobsPaginationRef.current.page === request.pipelineJobs.page
          && pipelineJobsPaginationRef.current.rowsPerPage === request.pipelineJobs.rowsPerPage
          && doorVisitsPaginationRef.current.page === request.doorVisits.page
          && doorVisitsPaginationRef.current.rowsPerPage === request.doorVisits.rowsPerPage;

        if (!isCurrentPagination) return;

        const clampedPipelineJobsPage = request.clampEmptyPipelineJobsPage === true
          ? resolveClampedPipelineJobsPage({
              itemCount: cycle.pipelineJobs.items.length,
              page: request.pipelineJobs.page,
              rowsPerPage: request.pipelineJobs.rowsPerPage,
              total: cycle.pipelineJobs.total,
            })
          : null;

        if (clampedPipelineJobsPage !== null) {
          const nextPipelineJobs = {
            ...request.pipelineJobs,
            page: clampedPipelineJobsPage,
          };
          pipelineJobsPaginationRef.current = nextPipelineJobs;
          setPipelineJobsPageState(clampedPipelineJobsPage);
          setData(cycle.detail);
          dataRef.current = cycle.detail;
          setDoorVisits(cycle.doorVisits);
          setError(null);
          setBackgroundError(null);
          refreshInFlightRef.current = false;
          activeCycleRef.current = null;
          void runRefreshCycle({
            ...request,
            kind: 'pagination',
            pipelineJobs: nextPipelineJobs,
            clampEmptyPipelineJobsPage: true,
          });
          return;
        }

        setData(cycle.detail);
        dataRef.current = cycle.detail;
        setPipelineJobs(cycle.pipelineJobs);
        setDoorVisits(cycle.doorVisits);
        setError(null);
        setBackgroundError(null);

        if (cycle.detail === null) {
          setAutomaticPollingEnabled(false);
          return;
        }

        if (deadlineRef.current === null) {
          const nextDeadline = scheduler.now() + CANVASSING_SESSION_POLL_DEADLINE_MS;
          deadlineRef.current = nextDeadline;
          setDeadline(nextDeadline);
        }

        if (!cycle.detail.pollingRecommended) {
          setAutomaticPollingEnabled(false);
        } else if (
          request.kind === 'manual'
          && deadlineRef.current !== null
          && scheduler.now() < deadlineRef.current
        ) {
          setAutomaticPollingEnabled(true);
        }
      } catch (requestError) {
        if (controller.signal.aborted || request.sessionId !== sessionIdRef.current) return;

        const normalizedError = toCanvassingSessionRequestError(requestError, 'Failed to load canvassing session details.');
        if (request.kind === 'initial' && dataRef.current === null) {
          setError(normalizedError);
        } else {
          setBackgroundError(normalizedError);
          if (request.kind === 'automatic') {
            setAutomaticPollingEnabled(false);
          }
        }
      } finally {
        if (activeCycleRef.current === activeCycle) {
          activeCycleRef.current = null;
          refreshInFlightRef.current = false;
          if (!controller.signal.aborted && request.sessionId === sessionIdRef.current) {
            setLoading(false);
            setRefreshing(false);
            setManualRefreshing(false);
            if (pendingClampedRefetchRef.current) {
              pendingClampedRefetchRef.current = false;
              void runRefreshCycle({
                kind: dataRef.current === null ? 'initial' : 'manual',
                sessionId: request.sessionId,
                pipelineJobs: pipelineJobsPaginationRef.current,
                doorVisits: doorVisitsPaginationRef.current,
                clampEmptyPipelineJobsPage: true,
              });
            }
          }
        }
      }
    },
    [fetchImpl, scheduler],
  );

  const startClampedRefetch = useCallback(
    (activeSessionId: string): void => {
      if (!isCanvassingSessionId(activeSessionId) || refreshInFlightRef.current) return;

      void runRefreshCycle({
        kind: dataRef.current === null ? 'initial' : 'manual',
        sessionId: activeSessionId,
        pipelineJobs: pipelineJobsPaginationRef.current,
        doorVisits: doorVisitsPaginationRef.current,
        clampEmptyPipelineJobsPage: true,
      });
    },
    [runRefreshCycle],
  );

  const stopAutomaticPolling = useCallback(
    (activeSessionId: string): void => {
      setAutomaticPollingEnabled(false);
      const activeCycle = activeCycleRef.current;
      if (activeCycle !== null && activeCycle.sessionId === activeSessionId) {
        abortActiveCycle();
        setRefreshing(false);
      }
      if (pendingClampedRefetchRef.current && sessionIdRef.current === activeSessionId) {
        pendingClampedRefetchRef.current = false;
        startClampedRefetch(activeSessionId);
      }
    },
    [abortActiveCycle, startClampedRefetch],
  );

  useEffect(() => {
    pendingClampedRefetchRef.current = false;
    abortActiveCycle();
    dataRef.current = null;
    deadlineRef.current = null;
    pipelineJobsPaginationRef.current = { page: 0, rowsPerPage: CANVASSING_SESSION_DETAIL_DEFAULT_ROWS_PER_PAGE };
    doorVisitsPaginationRef.current = { page: 0, rowsPerPage: CANVASSING_SESSION_DETAIL_DEFAULT_ROWS_PER_PAGE };
    setData(null);
    setPipelineJobs(emptyPipelineJobs());
    setDoorVisits(emptyDoorVisits());
    setError(null);
    setBackgroundError(null);
    setRefreshing(false);
    setManualRefreshing(false);
    setAutomaticPollingEnabled(true);
    setDeadline(null);
    setPipelineJobsPageState(0);
    setPipelineJobsRowsPerPageState(CANVASSING_SESSION_DETAIL_DEFAULT_ROWS_PER_PAGE);
    setDoorVisitsPageState(0);
    setDoorVisitsRowsPerPageState(CANVASSING_SESSION_DETAIL_DEFAULT_ROWS_PER_PAGE);

    if (!isValidSessionId) {
      setLoading(false);
      return undefined;
    }

    const initialRequest: RefreshRequest = {
      kind: 'initial',
      sessionId,
      pipelineJobs: { page: 0, rowsPerPage: CANVASSING_SESSION_DETAIL_DEFAULT_ROWS_PER_PAGE },
      doorVisits: { page: 0, rowsPerPage: CANVASSING_SESSION_DETAIL_DEFAULT_ROWS_PER_PAGE },
    };
    void runRefreshCycle(initialRequest);

    return () => {
      abortActiveCycle();
    };
  }, [abortActiveCycle, isValidSessionId, runRefreshCycle, sessionId]);

  useEffect(() => {
    if (deadline === null || !isValidSessionId) return undefined;

    const remainingMs = deadline - scheduler.now();
    if (remainingMs <= 0) {
      stopAutomaticPolling(sessionId);
      return undefined;
    }

    const deadlineTimer = scheduler.setTimeout(() => stopAutomaticPolling(sessionId), remainingMs);
    return () => scheduler.clearTimeout(deadlineTimer);
  }, [deadline, isValidSessionId, scheduler, sessionId, stopAutomaticPolling]);

  useEffect(() => {
    if (
      !isValidSessionId
      || data === null
      || !data.pollingRecommended
      || !automaticPollingEnabled
      || deadline === null
      || refreshing
    ) {
      return undefined;
    }

    const remainingMs = deadline - scheduler.now();
    if (remainingMs <= 0) {
      stopAutomaticPolling(sessionId);
      return undefined;
    }

    const pollTimer = scheduler.setTimeout(() => {
      void runRefreshCycle({
        kind: 'automatic',
        sessionId,
        pipelineJobs: pipelineJobsPaginationRef.current,
        doorVisits: doorVisitsPaginationRef.current,
      });
    }, Math.min(CANVASSING_SESSION_POLL_INTERVAL_MS, remainingMs));

    return () => scheduler.clearTimeout(pollTimer);
  }, [automaticPollingEnabled, data, deadline, isValidSessionId, refreshing, runRefreshCycle, scheduler, sessionId, stopAutomaticPolling]);

  const requestPaginationRefresh = useCallback(
    (nextPipelineJobs: Pagination, nextDoorVisits: Pagination): void => {
      if (!isValidSessionId || refreshInFlightRef.current) return;

      void runRefreshCycle({
        kind: 'pagination',
        sessionId,
        pipelineJobs: nextPipelineJobs,
        doorVisits: nextDoorVisits,
      });
    },
    [isValidSessionId, runRefreshCycle, sessionId],
  );

  const setPipelineJobsPage = useCallback(
    (nextPage: number): void => {
      const page = normalizePage(nextPage);
      if (page === pipelineJobsPaginationRef.current.page || refreshInFlightRef.current) return;

      const nextPipelineJobs = { ...pipelineJobsPaginationRef.current, page };
      pipelineJobsPaginationRef.current = nextPipelineJobs;
      setPipelineJobsPageState(page);
      requestPaginationRefresh(nextPipelineJobs, doorVisitsPaginationRef.current);
    },
    [requestPaginationRefresh],
  );

  const setPipelineJobsRowsPerPage = useCallback(
    (nextRowsPerPage: number): void => {
      const rowsPerPage = normalizeRowsPerPage(nextRowsPerPage);
      if (rowsPerPage === pipelineJobsPaginationRef.current.rowsPerPage || refreshInFlightRef.current) return;

      const nextPipelineJobs = { page: 0, rowsPerPage };
      pipelineJobsPaginationRef.current = nextPipelineJobs;
      setPipelineJobsPageState(0);
      setPipelineJobsRowsPerPageState(rowsPerPage);
      requestPaginationRefresh(nextPipelineJobs, doorVisitsPaginationRef.current);
    },
    [requestPaginationRefresh],
  );

  const setDoorVisitsPage = useCallback(
    (nextPage: number): void => {
      const page = normalizePage(nextPage);
      if (page === doorVisitsPaginationRef.current.page || refreshInFlightRef.current) return;

      const nextDoorVisits = { ...doorVisitsPaginationRef.current, page };
      doorVisitsPaginationRef.current = nextDoorVisits;
      setDoorVisitsPageState(page);
      requestPaginationRefresh(pipelineJobsPaginationRef.current, nextDoorVisits);
    },
    [requestPaginationRefresh],
  );

  const setDoorVisitsRowsPerPage = useCallback(
    (nextRowsPerPage: number): void => {
      const rowsPerPage = normalizeRowsPerPage(nextRowsPerPage);
      if (rowsPerPage === doorVisitsPaginationRef.current.rowsPerPage || refreshInFlightRef.current) return;

      const nextDoorVisits = { page: 0, rowsPerPage };
      doorVisitsPaginationRef.current = nextDoorVisits;
      setDoorVisitsPageState(0);
      setDoorVisitsRowsPerPageState(rowsPerPage);
      requestPaginationRefresh(pipelineJobsPaginationRef.current, nextDoorVisits);
    },
    [requestPaginationRefresh],
  );

  const refetch = useCallback((options?: CanvassingSessionDetailRefetchOptions): void => {
    if (!isValidSessionId) return;

    if (refreshInFlightRef.current) {
      if (options?.clampEmptyPipelineJobsPage === true) {
        pendingClampedRefetchRef.current = true;
      }
      return;
    }

    void runRefreshCycle({
      kind: dataRef.current === null ? 'initial' : 'manual',
      sessionId,
      pipelineJobs: pipelineJobsPaginationRef.current,
      doorVisits: doorVisitsPaginationRef.current,
      clampEmptyPipelineJobsPage: options?.clampEmptyPipelineJobsPage === true,
    });
  }, [isValidSessionId, runRefreshCycle, sessionId]);

  return {
    data,
    pipelineJobs,
    doorVisits,
    loading,
    refreshing,
    manualRefreshing,
    error,
    backgroundError,
    pipelineJobsPage,
    pipelineJobsRowsPerPage,
    doorVisitsPage,
    doorVisitsRowsPerPage,
    setPipelineJobsPage,
    setPipelineJobsRowsPerPage,
    setDoorVisitsPage,
    setDoorVisitsRowsPerPage,
    refetch,
  };
};
